/* eslint-disable replo/no-tailwind-literal-color */
import type { AgentState } from "@agent-ui/features/chat/hooks/useReploAgentConnection";

import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Field, FieldLabel } from "@replo/design-system/components/field/Field";
import { Input } from "@replo/design-system/components/input/Input";
import {
  useDebugPublishStatusOverride,
  useDebugPublishStatusOverrideActions,
} from "@v2/features/debug/debugPublishStatusOverrideStore";

const PUBLISH_STATUSES = [
  "idle",
  "building",
  "publishing",
  "published",
  "failed",
] as const;

const PublishTab: React.FC<{
  realPublishStatus: AgentState["publishStatus"];
}> = ({ realPublishStatus }) => {
  const override = useDebugPublishStatusOverride();
  const { setPublishStatusOverride, clearPublishStatusOverride } =
    useDebugPublishStatusOverrideActions();
  const [errorMessage, setErrorMessage] = React.useState("");

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-auto text-xs">
      <div>
        <div className="mb-2 text-xs font-semibold text-slate-500">
          Override Publish Status
        </div>
        <div className="flex flex-wrap gap-1.5">
          {PUBLISH_STATUSES.map((status) => (
            <Button
              key={status}
              variant={override?.status === status ? "primary" : "secondary"}
              size="sm"
              onClick={() => {
                setPublishStatusOverride({
                  status,
                  startedAt: Date.now(),
                  ...(status === "published" || status === "failed"
                    ? { completedAt: Date.now() }
                    : {}),
                  ...(status === "failed" && errorMessage
                    ? { errorMessage }
                    : {}),
                });
              }}
            >
              {status}
            </Button>
          ))}
        </div>
      </div>

      <Field size="xs">
        <FieldLabel size="xs">Error Message (for "failed" status)</FieldLabel>
        <Input
          size="sm"
          placeholder="Optional error message…"
          value={errorMessage}
          onChange={(e) => setErrorMessage(e.target.value)}
        />
      </Field>

      {override && (
        <Button
          variant="destructive"
          size="sm"
          UNSAFE_className="self-start"
          onClick={clearPublishStatusOverride}
        >
          Clear Override
        </Button>
      )}

      <div className="mt-2 border-t border-slate-200 pt-3">
        <div className="mb-1 text-xs font-semibold text-slate-500">
          Real DO State:
        </div>
        <pre className="overflow-auto whitespace-pre-wrap break-words rounded bg-slate-50 p-2 text-xs text-slate-700">
          {realPublishStatus
            ? JSON.stringify(realPublishStatus, null, 2)
            : "null (no publish status)"}
        </pre>
      </div>

      {override && (
        <div>
          <div className="mb-1 text-xs font-semibold text-slate-500">
            Active Override:
          </div>
          <pre className="overflow-auto whitespace-pre-wrap break-words rounded bg-slate-50 p-2 text-xs text-blue-700">
            {JSON.stringify(override, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export { PublishTab };
